import type { ArchetypeConfig, ArchetypeId, FallbackFormField, TriggerArchetypeComponent } from "./types";
import { SimpleButton } from "./SimpleButton";
import { SchemaDrivenForm } from "./SchemaDrivenForm";
import { ChatBotGuide } from "./ChatBotGuide";
import { FormInput } from "./FormInput";
import { HistoricalPicker } from "./HistoricalPicker";
import { EntitySearch } from "./EntitySearch";
import { LiveListener } from "./LiveListener";
import { AnySpendPayment } from "./AnySpendPayment";
import { ShopifyInventoryAdjust } from "./ShopifyInventoryAdjust";
import { SlackInteraction } from "./SlackInteraction";
import { StripePaymentForm } from "./StripePaymentForm";
import { TokenPriceAlert } from "./TokenPriceAlert";
import { evmLogFallback } from "./fallbacks/evmLogFallback";
import { evmTransferFallback } from "./fallbacks/evmTransferFallback";
import { farcasterCastFallback } from "./fallbacks/farcasterCastFallback";
import { farcasterFollowerFallback } from "./fallbacks/farcasterFollowerFallback";
import { polymarketUserBetFallback } from "./fallbacks/polymarketUserBetFallback";
import { polymarketMarketTradeFallback } from "./fallbacks/polymarketMarketTradeFallback";
import { polymarketNewMarketFallback } from "./fallbacks/polymarketNewMarketFallback";
import { polymarketMarketCloseFallback } from "./fallbacks/polymarketMarketCloseFallback";
import { shopifyOrderFallback } from "./fallbacks/shopifyOrderFallback";
import { slackChannelCreatedFallback } from "./fallbacks/slackChannelCreatedFallback";
import { slackReactionFallback } from "./fallbacks/slackReactionFallback";
import { slackUserAddedFallback } from "./fallbacks/slackUserAddedFallback";
import { slackUserMentionFallback } from "./fallbacks/slackUserMentionFallback";
import { coinbasePaymentFallback } from "./fallbacks/coinbasePaymentFallback";
import { coinbaseCheckoutFallback } from "./fallbacks/coinbaseCheckoutFallback";
import { twitterSearchFallback } from "./fallbacks/twitterSearchFallback";
import { exchangeListingFallback } from "./fallbacks/exchangeListingFallback";
import { unusualWhalesNewsFallback, unusualWhalesFlowFallback } from "./fallbacks/unusualWhalesFallback";
import { emailMatchingOverrides } from "./overrides/emailMatchingOverrides";
import { emailReceivedOverrides } from "./overrides/emailReceivedOverrides";

// ============================================================================
// Trigger-specific fallback form field sets
// ============================================================================
// Defined here (alongside the registry) so the per-trigger config below stays
// declarative. Each set lists the fields a trigger surfaces in its manual
// fallback form when the historical picker has no real data to show.

// Field keys match the gmail-new-email-received resultSchema in
// services/b3os-workflow/configs/json/triggers.json (note: the body field is
// `text`, not `body`). `triggeredAt` is added by FallbackForm automatically.
const gmailFallbackFields: FallbackFormField[] = [
  {
    key: "from",
    label: "From",
    type: "email",
    placeholder: "sender@example.com",
    description: "Email address of the sender (From header value).",
  },
  {
    key: "to",
    label: "To",
    type: "email",
    placeholder: "you@example.com",
    description: "Email address that received the message (To header value).",
  },
  {
    key: "subject",
    label: "Subject",
    type: "text",
    placeholder: "Your invoice for March is ready",
  },
  {
    key: "text",
    label: "Body",
    type: "textarea",
    placeholder: "The full plain-text message body...",
    description: "Plain text body of the email (matches the trigger's `text` field).",
  },
];

// ============================================================================
// Archetype Component Map
// ============================================================================

const ARCHETYPE_COMPONENTS: Record<ArchetypeId, TriggerArchetypeComponent> = {
  "simple-button": SimpleButton,
  "schema-driven-form": SchemaDrivenForm,
  "chatbot-guide": ChatBotGuide,
  "form-input": FormInput,
  "historical-picker": HistoricalPicker,
  "entity-search": EntitySearch,
  "live-listener": LiveListener,
  "anyspend-payment": AnySpendPayment,
  "stripe-payment-form": StripePaymentForm,
  "slack-interaction": SlackInteraction,
  "shopify-inventory-adjust": ShopifyInventoryAdjust,
  "token-price-alert": TokenPriceAlert,
};

// ============================================================================
// Archetype Registry — maps every trigger type to its archetype config
// ============================================================================
// To add a new trigger: add one entry here.
// To add a new archetype: add the ID to ArchetypeId (types.ts), create the
// component, add it to ARCHETYPE_COMPONENTS above, then map triggers to it.

const ARCHETYPE_REGISTRY: Record<string, ArchetypeConfig> = {
  // === SchemaDrivenForm / Manual + scheduled triggers ===
  // These triggers either accept user-defined inputs or none at all. The
  // SchemaDrivenForm archetype renders a form when inputSchema has fields
  // and a "click to run" message when it doesn't, with an "Advanced (payload)"
  // disclosure available in both modes.
  manual: {
    archetype: "schema-driven-form",
    liveArchetype: "live-listener",
    description: "Enter input data to test your workflow",
    // Manual trigger resultSchema is { triggeredAt, body } with
    // additionalProperties: false — see services/b3os-workflow/configs/json/triggers.json:21
    payloadWrapperKey: "body",
    hideExecutionControlsWhenPublished: false,
  },
  cronjob: {
    archetype: "schema-driven-form",
    description: "Click run to test your scheduled workflow with a fresh timestamp.",
    requiresModal: true,
  },
  schedule: {
    archetype: "schema-driven-form",
    description: "Click run to test your scheduled workflow with a fresh timestamp.",
    requiresModal: true,
  },
  anyspend: {
    archetype: "anyspend-payment",
    description: "Simulates a payment event to test your workflow.",
    hideExecutionControlsWhenPublished: true,
    requiresModal: true,
  },
  "ramp-new-transaction": {
    archetype: "simple-button",
    description: "Simulates a new Ramp transaction event.",
  },
  "ramp-transaction-status-updated": {
    archetype: "simple-button",
    description: "Simulates a Ramp transaction status change.",
  },
  "ramp-transfer-payment-updated": {
    archetype: "simple-button",
    description: "Simulates a Ramp transfer payment update.",
  },

  // === ChatBotGuide ===
  "telegram-channel": {
    archetype: "chatbot-guide",
    description: "Send a message to your bot, then click Run to capture it.",
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-direct-message": {
    archetype: "chatbot-guide",
    description: "Send a direct message to your Slack bot, then click Run.",
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-user-mention": {
    archetype: "historical-picker",
    description: "Browse recent messages that mention the configured user.",
    fetchFallbackData: slackUserMentionFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-interaction-event": {
    archetype: "slack-interaction",
    description: "Click a button to simulate a Slack interaction event.",
    headerVisual: { kind: "connector-logo" },
  },

  // === FormInput ===
  "token-price-cexes": {
    archetype: "token-price-alert",
    description: "Set a test price to simulate your price alert.",
    disableRunButtonWhenPublished: true,
  },
  "farcaster-new-cast": {
    archetype: "historical-picker",
    description: "Browse recent Farcaster casts to use as test data.",
    fetchFallbackData: farcasterCastFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "farcaster-new-follower": {
    archetype: "historical-picker",
    description: "Browse recent followers to use as test data.",
    fetchFallbackData: farcasterFollowerFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "farcaster-new-reaction": {
    archetype: "historical-picker",
    description: "Choose a cast and simulate a like or recast.",
    fetchFallbackData: farcasterCastFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "polymarket-user-bet": {
    archetype: "historical-picker",
    description: "Browse recent positions for the tracked trader.",
    headerVisual: { kind: "connector-logo" },
    fetchFallbackData: polymarketUserBetFallback,
  },
  "polymarket-market-trade": {
    archetype: "historical-picker",
    description: "Browse recent trades on the configured market.",
    headerVisual: { kind: "connector-logo" },
    fetchFallbackData: polymarketMarketTradeFallback,
  },
  "polymarket-new-market": {
    archetype: "historical-picker",
    description: "Browse recently created Polymarket markets.",
    headerVisual: { kind: "connector-logo" },
    fetchFallbackData: polymarketNewMarketFallback,
  },
  "polymarket-market-close": {
    archetype: "historical-picker",
    description: "Browse recently closed or resolved Polymarket markets.",
    headerVisual: { kind: "connector-logo" },
    fetchFallbackData: polymarketMarketCloseFallback,
  },
  "unusual-whales-market-news": {
    archetype: "historical-picker",
    description: "Browse recent market news headlines from Unusual Whales.",
    fetchFallbackData: unusualWhalesNewsFallback,
    emptyStateMessage: "No recent headlines match your filters. Try removing a filter or check back later.",
  },
  "unusual-whales-options-flow-alerts": {
    archetype: "historical-picker",
    description: "Browse recent options flow alerts from Unusual Whales.",
    fetchFallbackData: unusualWhalesFlowFallback,
    emptyStateMessage: "No recent flow alerts match your filters. Try removing a filter or check back later.",
  },
  "email-new-email-received": {
    archetype: "form-input",
    description: "Provide sample email data to test your email workflow.",
    sampleOverrides: emailReceivedOverrides,
    headerVisual: { kind: "connector-logo" },
  },
  "email-new-email-matching-criteria": {
    archetype: "form-input",
    description: "Provide sample email data matching your filter criteria.",
    sampleOverrides: emailMatchingOverrides,
    headerVisual: { kind: "connector-logo" },
  },
  "stripe-payment-receive": {
    archetype: "stripe-payment-form",
    description: "Test with a quick amount or paste a custom Stripe event payload.",
  },
  "coinbase-payment-received": {
    archetype: "historical-picker",
    liveArchetype: "historical-picker",
    description: "Browse recent payment links from your Coinbase Business account.",
    fetchFallbackData: coinbasePaymentFallback,
    emptyStateMessage:
      "No payment links found. Create a payment link in Coinbase Business, then come back and try again.",
  },
  "coinbase-checkout-received": {
    archetype: "historical-picker",
    liveArchetype: "historical-picker",
    description: "Browse recent checkouts from your Coinbase Business account.",
    fetchFallbackData: coinbaseCheckoutFallback,
    emptyStateMessage:
      "No checkouts found. Create a checkout first using the Coinbase Business dashboard or the create-checkout action.",
  },

  // === HistoricalPicker ===
  "exchange-listing": {
    archetype: "historical-picker",
    description: "Pick a trading pair to test your workflow.",
    fetchFallbackData: exchangeListingFallback,
    requiresModal: true,
    emptyStateMessage: "No trading pairs found for this exchange.",
  },
  "x-new-tweet": {
    archetype: "historical-picker",
    description: "Search and pick a tweet to test your workflow.",
    fetchFallbackData: twitterSearchFallback,
    headerVisual: { kind: "connector-logo" },
    requiresModal: true,
    emptyStateMessage: "No tweets found. Try a different username or keyword.",
  },
  "evm-log": {
    archetype: "historical-picker",
    description: "Browse recent contract events or paste a transaction to replay.",
    fetchFallbackData: evmLogFallback,
  },
  "erc20-receive": {
    archetype: "historical-picker",
    description: "Browse recent token transfers received by your address.",
    fetchFallbackData: evmTransferFallback,
  },
  "erc20-send": {
    archetype: "historical-picker",
    description: "Browse recent token transfers sent from your address.",
    fetchFallbackData: evmTransferFallback,
  },
  "eth-receive": {
    archetype: "historical-picker",
    description: "Browse recent ETH transfers received by your address.",
    fetchFallbackData: evmTransferFallback,
  },
  "eth-send": {
    archetype: "historical-picker",
    description: "Browse recent ETH transfers sent from your address.",
    fetchFallbackData: evmTransferFallback,
  },
  "slack-new-message-in-channels": {
    archetype: "chatbot-guide",
    description: "Type a sample Slack message to test your workflow.",
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-keyword-mention": {
    archetype: "chatbot-guide",
    description: "Type a sample Slack keyword mention to test your workflow.",
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-reaction-added": {
    archetype: "historical-picker",
    description: "Browse recent Slack reactions to pick test data.",
    fetchFallbackData: slackReactionFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-channel-created": {
    archetype: "historical-picker",
    description: "Browse recently created Slack channels.",
    fetchFallbackData: slackChannelCreatedFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "slack-new-user-added": {
    archetype: "historical-picker",
    description: "Browse recently added Slack users.",
    fetchFallbackData: slackUserAddedFallback,
    headerVisual: { kind: "connector-logo" },
  },
  "gmail-new-email-received": {
    archetype: "historical-picker",
    description: "Browse recent Gmail messages, or fill in test data manually below.",
    headerVisual: { kind: "connector-logo" },
    fallbackFormFields: gmailFallbackFields,
  },
  "gmail-new-email-matching-search": {
    archetype: "historical-picker",
    description: "Browse recent Gmail messages, or fill in test data manually below.",
    headerVisual: { kind: "connector-logo" },
    fallbackFormFields: gmailFallbackFields,
  },

  // === Shopify (HistoricalPicker with fallback fetchers) ===
  "shopify-order-created": {
    archetype: "historical-picker",
    description: "Browse recent orders from your Shopify store.",
    headerVisual: { kind: "connector-logo" },
    fetchFallbackData: shopifyOrderFallback,
    emptyStateMessage: "No orders found. Go to your Shopify store and create an order, then come back and try again.",
  },
  "shopify-order-paid": {
    archetype: "historical-picker",
    description: "Browse recent paid orders from your Shopify store.",
    headerVisual: { kind: "connector-logo" },
    fetchFallbackData: shopifyOrderFallback,
    emptyStateMessage:
      "No paid orders found. Go to your Shopify store and complete an order, then come back and try again.",
  },
  "shopify-inventory-level-updated": {
    archetype: "shopify-inventory-adjust",
    liveArchetype: "live-listener",
    description: "Search for a product and simulate an inventory change.",
    headerVisual: { kind: "connector-logo" },
  },
};

// ============================================================================
// Public API
// ============================================================================

const DEFAULT_CONFIG: ArchetypeConfig = {
  archetype: "simple-button",
  description: "Run a test with generated sample data.",
};

/**
 * Get the archetype config for a trigger type.
 * Falls back to SimpleButton for unknown triggers.
 */
export function getArchetypeConfig(triggerType: string): ArchetypeConfig {
  return ARCHETYPE_REGISTRY[triggerType] ?? DEFAULT_CONFIG;
}

/**
 * Resolve the active archetype component for a trigger, accounting for
 * live-mode overrides when the workflow is published.
 */
export function resolveArchetype(
  triggerType: string,
  isPublished: boolean,
): { Component: TriggerArchetypeComponent; config: ArchetypeConfig } {
  const config = getArchetypeConfig(triggerType);
  const activeId = isPublished && config.liveArchetype ? config.liveArchetype : config.archetype;
  const Component = ARCHETYPE_COMPONENTS[activeId];
  return { Component, config };
}
